'use client';

import type { ReactNode } from 'react';
import { useRef, useState } from 'react';

import { MarketingImage } from '~/(marketing)/_components/ui/marketing-image';

import { MARKETING_IMG } from '../../../_lib/marketing-utils';

/* Localized content shapes (mirrored from ../../_content.tsx – kept inline so
   this client module never imports the server content tree). */
export interface LifecycleTab {
  trigger: string;
  heading: string;
  body: ReactNode;
  keepBody: ReactNode;
  statValue: string;
  statLabel: string;
  imageAlt: string | null;
}

export interface LifecycleCalendar {
  head: string;
  slotsBadge: string;
  rows: {
    day: string;
    time: string;
    title: string;
    meta: string;
    status: string;
    statusKind: 'ok' | 'open' | 'ask';
  }[];
}

export interface LifecycleDocstack {
  docs: { name: string; meta: string; kind: 'ok' | 'pending' | 'bond' }[];
}

export interface LifecycleTabsProps {
  regionLabel: string;
  keepLabel: string;
  freenote: string;
  tabs: LifecycleTab[];
  calendar: LifecycleCalendar;
  docstack: LifecycleDocstack;
}

/** Photos for the photo-based tabs, in tab order (null = abstract visual). */
const TAB_IMAGES: (string | null)[] = [
  `${MARKETING_IMG}/slot-6-leer-altbau-fensterlicht.jpg`,
  `${MARKETING_IMG}/slot-9-hands-typing-laptop.jpg`,
  null,
  `${MARKETING_IMG}/slot-7-hand-notizbuch.jpg`,
  null,
  `${MARKETING_IMG}/slot-8-kitchen-detail.jpg`,
];

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <path d="M21 12a9 9 0 1 1-9-9" />
      <polyline points="21 3 21 9 15 9" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function DocIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function MessageIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

/** Stat icons in tab order. */
const STAT_ICONS = [
  <ClockIcon key="0" />,
  <RefreshIcon key="1" />,
  <CalendarIcon key="2" />,
  <CheckIcon key="3" />,
  <DocIcon key="4" />,
  <MessageIcon key="5" />,
];

function TabStatCard({
  icon,
  value,
  label,
}: {
  icon: ReactNode;
  value: string;
  label: string;
}) {
  return (
    <div className="tab-statcard">
      <span className="tab-statcard-icon">{icon}</span>
      <span className="tab-statcard-text">
        <span className="tab-statcard-value">{value}</span>
        <span className="tab-statcard-label">{label}</span>
      </span>
    </div>
  );
}

function KeepBlock({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="tab-keepblock">
      <span className="tab-keepblock-label">{label}</span>
      <p>{children}</p>
    </div>
  );
}

/** Abstract calendar visual (tab 3) – localized labels via props. */
function CalendarVisual({ calendar }: { calendar: LifecycleCalendar }) {
  const statusClass: Record<'ok' | 'open' | 'ask', string> = {
    ok: 'av-cal-status av-cal-status--ok',
    open: 'av-cal-status',
    ask: 'av-cal-status av-cal-status--ask',
  };
  const rowClass: Record<'ok' | 'open' | 'ask', string> = {
    ok: 'av-cal-row av-cal-row--active',
    open: 'av-cal-row',
    ask: 'av-cal-row av-cal-row--ask',
  };

  return (
    <div className="tab-figure tab-figure--abstract">
      <div className="av-frame">
        <div className="av-calendar" aria-hidden="true">
          <div className="av-cal-head">
            <strong>{calendar.head}</strong>
            <span>{calendar.slotsBadge}</span>
          </div>
          {calendar.rows.map((row, i) => (
            <div key={i} className={rowClass[row.statusKind]}>
              <div className="av-cal-day">
                {row.day}
                <br />
                <span
                  style={{
                    fontWeight: 400,
                    color: 'var(--muted-foreground)',
                    fontSize: '11px',
                  }}
                >
                  {row.time}
                </span>
              </div>
              <div>
                <div style={{ fontWeight: 600, color: 'var(--foreground)' }}>
                  {row.title}
                </div>
                <div className="av-cal-meta">{row.meta}</div>
              </div>
              <span
                className={statusClass[row.statusKind]}
                style={
                  row.statusKind === 'open'
                    ? {
                        background: 'var(--muted)',
                        color: 'var(--muted-foreground)',
                      }
                    : undefined
                }
              >
                {row.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Abstract document-stack visual (tab 5) – localized labels via props. */
function DocstackVisual({ docstack }: { docstack: LifecycleDocstack }) {
  const docIcon: Record<'ok' | 'pending' | 'bond', ReactNode> = {
    ok: (
      <svg viewBox="0 0 24 24">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    ),
    pending: (
      <svg viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ),
    bond: (
      <svg viewBox="0 0 24 24">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
  };
  const iconClass: Record<'ok' | 'pending' | 'bond', string> = {
    ok: 'av-doc-icon av-doc-icon--ok',
    pending: 'av-doc-icon av-doc-icon--pending',
    bond: 'av-doc-icon av-doc-icon--bond',
  };

  return (
    <div className="tab-figure tab-figure--abstract">
      <div className="av-frame">
        <div className="av-docstack" aria-hidden="true">
          {docstack.docs.map((doc, i) => (
            <div key={i} className="av-doc">
              <span className={iconClass[doc.kind]}>{docIcon[doc.kind]}</span>
              <span className="av-doc-name">{doc.name}</span>
              <span className="av-doc-meta">{doc.meta}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Service lifecycle – six pillars as tabs (prototype service.html §2). Roving
 * tabindex + arrow-key nav; `is-active` (and the [data-active] attribute) drive
 * which panel shows. Locale-agnostic: all copy comes in via props from the
 * server parent (ServicePage). Icons, images and structure stay here.
 */
/**
 * 🔴 **ZWEI BAUTEILE DIESES NAMENS — und sie bleiben getrennt.** Bis zum
 * 21.09.2026 hiessen beide `LifecycleTabs`; das hat einmal schon zu einer
 * Suche in der falschen Datei geführt. Gemessen, warum sie getrennt bleiben:
 * **null gemeinsame Zeilen**, andere Datenform (`LifecyclePanel` gegen
 * `LifecycleTab`), andere Gestalt (`statcard*`/`dash*` gegen `tab-statcard*`/
 * `av-*`) und andere ARIA-Rollen (`tabpanel` gegen `region`).
 *
 * ⚠️ **Die Begründung steht in BEIDEN Dateien** — das ist die Lehre aus
 * `EmailSearchFilters`: eine Begründung, die man erst findet, nachdem man die
 * Arbeit begonnen hat, kommt zu spät. Der Name ist deshalb zusätzlich
 * unterschieden.
 *
 * Diese hier: die Seite „So funktioniert’s“. Schwester:
 * `_components/fuer-grossvermieter/lifecycle-tabs.tsx`.
 *
 * ⚠️ **Mit-Befund, nicht hier gelöst:** diese Fassung trägt `role="tablist"`
 * und `role="tab"`, aber **kein** `role="tabpanel"` — die Tafel darunter ist
 * eine `region`. Die Schwester macht es vollständig. Eigener Vorgang.
 */
export function LifecycleTabsService({
  regionLabel,
  keepLabel,
  freenote,
  tabs,
  calendar,
  docstack,
}: LifecycleTabsProps) {
  const [active, setActive] = useState(0);
  const triggerRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const onKey = (e: React.KeyboardEvent, i: number) => {
    let next: number | null = null;
    if (e.key === 'ArrowRight') next = (i + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;
    if (next !== null) {
      e.preventDefault();
      setActive(next);
      triggerRefs.current[next]?.focus();
    }
  };

  const panelProps = (i: number) => ({
    className: `tab-panel${i === active ? ' is-active' : ''}`,
    role: 'tabpanel' as const,
    id: `tab-panel-${i + 1}`,
    'aria-labelledby': `tab-trigger-${i + 1}`,
    'data-active': i === active,
    hidden: i !== active,
  });

  return (
    <div className="tabs reveal" role="region" aria-label={regionLabel}>
      <div className="tabs-bar" role="tablist">
        {tabs.map((tab, i) => (
          <button
            key={tab.trigger}
            ref={(el) => {
              triggerRefs.current[i] = el;
            }}
            className="tab-trigger"
            role="tab"
            id={`tab-trigger-${i + 1}`}
            aria-selected={i === active}
            aria-controls={`tab-panel-${i + 1}`}
            data-tab={i + 1}
            tabIndex={i === active ? 0 : -1}
            onClick={() => setActive(i)}
            onKeyDown={(e) => onKey(e, i)}
          >
            <span className="tab-trigger-num">{i + 1}</span> {tab.trigger}
          </button>
        ))}
      </div>

      {tabs.map((tab, i) => {
        const image = TAB_IMAGES[i];
        const isFirst = i === 0;

        return (
          <div key={tab.trigger} {...panelProps(i)}>
            <div className="tab-panel-body">
              <div className="tab-copy">
                <h3>{tab.heading}</h3>
                <p>{tab.body}</p>
                <KeepBlock label={keepLabel}>{tab.keepBody}</KeepBlock>
                {isFirst ? (
                  <span className="tab-freenote">
                    <svg viewBox="0 0 24 24">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {freenote}
                  </span>
                ) : null}
              </div>
              <div className="tab-visual">
                {image ? (
                  <div className="tab-figure">
                    <MarketingImage
                      src={image}
                      alt={tab.imageAlt ?? ''}
                      loading="lazy"
                    />
                  </div>
                ) : i === 2 ? (
                  <CalendarVisual calendar={calendar} />
                ) : (
                  <DocstackVisual docstack={docstack} />
                )}
                <TabStatCard
                  icon={STAT_ICONS[i]}
                  value={tab.statValue}
                  label={tab.statLabel}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
